import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { TextArea } from "../../TextArea";
import { FileText, CheckCircle, Loader2, X } from "lucide-react";
import { api } from "../../../services/api";

interface Sigla {
  id: string;
  sigla: string;
  descricao: string;
  id_habbo_variavel?: number | null;
  status: string;
  criado_em: string;
  alterado_em: string;
}

interface FormValues {
  sigla: string;
  descricao: string;
  id_habbo_variavel: string;
}

interface SiglaModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingSigla?: Sigla | null;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  sigla: Yup.string()
    .required("Sigla é obrigatória")
    .min(1, "Sigla deve ter pelo menos 1 caractere")
    .max(20, "Sigla deve ter no máximo 20 caracteres"),
  descricao: Yup.string()
    .required("Descrição é obrigatória")
    .min(5, "Descrição deve ter pelo menos 5 caracteres")
    .max(500, "Descrição deve ter no máximo 500 caracteres"),
  id_habbo_variavel: Yup.string().matches(/^\d*$/, "Informe um número inteiro maior ou igual a 0"),
});

export function SiglaModal({ isOpen, onClose, editingSigla = null, onSuccess }: SiglaModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormValues>({
    sigla: "",
    descricao: "",
    id_habbo_variavel: "",
  });
  const [errors, setErrors] = useState<Partial<FormValues>>({});

  useEffect(() => {
    if (editingSigla) {
      setFormData({
        sigla: editingSigla.sigla,
        descricao: editingSigla.descricao,
        id_habbo_variavel:
          editingSigla.id_habbo_variavel !== null && editingSigla.id_habbo_variavel !== undefined
            ? String(editingSigla.id_habbo_variavel)
            : "",
      });
    } else {
      setFormData({
        sigla: "",
        descricao: "",
        id_habbo_variavel: "",
      });
    }
    setErrors({});
  }, [editingSigla, isOpen]);

  const validateForm = () => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: Partial<FormValues> = {};
      error.inner.forEach((err: any) => {
        validationErrors[err.path as keyof FormValues] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleInputChange = (field: keyof FormValues, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const payload = {
        ...formData,
        id_habbo_variavel: formData.id_habbo_variavel !== "" ? parseInt(formData.id_habbo_variavel, 10) : null,
      };

      if (editingSigla) {
        await api.put(`/siglas/${editingSigla.id}`, payload);
        enqueueSnackbar("Sigla atualizada com sucesso!", { variant: "success" });
      } else {
        await api.post("/siglas", payload);
        enqueueSnackbar("Sigla criada com sucesso!", { variant: "success" });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar sigla", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ sigla: "", descricao: "", id_habbo_variavel: "" });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {editingSigla ? "Editar Sigla" : "Nova Sigla"}
            </CardTitle>
            <CardDescription>
              {editingSigla
                ? "Atualize as informações da sigla"
                : "Crie uma nova sigla do sistema"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={submitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sigla">Sigla</Label>
              <Input
                id="sigla"
                value={formData.sigla}
                onChange={(e) => handleInputChange("sigla", e.target.value)}
                placeholder="Ex: PMRJ, CBMERJ, EB, <EB1>..."
                className={`font-mono text-lg ${errors.sigla ? "border-red-300" : ""}`}
                disabled={submitting}
              />
              {errors.sigla && (
                <p className="text-sm text-red-600">{errors.sigla}</p>
              )}
            </div>

            <div className="space-y-2">
              <TextArea
                name="descricao"
                title="Descrição"
                value={formData.descricao}
                onChange={(e) => handleInputChange("descricao", e.target.value)}
                placeholder="Digite a descrição completa da sigla..."
                error={errors.descricao}
                className="min-h-[100px]"
                disabled={submitting}
              />
              {errors.descricao && (
                <p className="text-sm text-red-600">{errors.descricao}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="id_habbo_variavel">ID da Variável no Habbo</Label>
              <Input
                id="id_habbo_variavel"
                type="number"
                min={0}
                value={formData.id_habbo_variavel}
                onChange={(e) => handleInputChange("id_habbo_variavel", e.target.value)}
                placeholder="Ex: 1, 2, 3... (opcional)"
                className={errors.id_habbo_variavel ? "border-red-300" : ""}
                disabled={submitting}
              />
              {errors.id_habbo_variavel && (
                <p className="text-sm text-red-600">{errors.id_habbo_variavel}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Valor que representa esta sigla na variável do jogo. Deixe em branco se não houver.
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingSigla ? "Atualizando..." : "Criando..."}
                  </>
                ) : (
                  editingSigla ? "Atualizar" : "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
